import { useState, useRef, useEffect, useCallback } from 'react';

// Zalo 등 자체 확인 팝업을 띄우는 인앱 브라우저를 고려한 대기 시간
const INTENT_TIMEOUT_MS = 5000;

/**
 * 안드로이드 환경에서 Chrome 외부 브라우저를 intent 스킴으로 호출하고
 * 백그라운드 전환 여부를 감지하여 실패 시 콜백을 실행하는 커스텀 훅
 */
export function useOpenInExternalBrowser() {
  const [isOpening, setIsOpening] = useState(false);
  const isMountedRef = useRef(true);
  const timerRef = useRef(null);
  const activeCleanupRef = useRef(null);

  useEffect(() => {
    isMountedRef.current = true;

    return () => {
      isMountedRef.current = false;
      if (timerRef.current) {
        clearTimeout(timerRef.current);
        timerRef.current = null;
      }
      if (activeCleanupRef.current) {
        activeCleanupRef.current();
        activeCleanupRef.current = null;
      }
    };
  }, []);

  const openInBrowser = useCallback((onFail, options = {}) => {
    if (typeof window === 'undefined') return;
    if (isOpening) return; // (가) 연타 차단

    setIsOpening(true); // (나) 잠금

    try {
      const targetUrl = new URL(options.url || window.location.href);
      if (options.autoOAuth ?? true) {
        targetUrl.searchParams.set('auto_oauth', '1');
      }

      const scheme = targetUrl.protocol.replace(':', '') || 'https';
      const urlWithoutScheme = targetUrl.href.replace(/^https?:\/\//i, '');

      // (다) browser_fallback_url 미사용 (제자리걸음 방지)
      const intentUrl = `intent://${urlWithoutScheme}#Intent;scheme=${scheme};package=com.android.chrome;end`;

      // 기존 활성 리스너/타이머 정리
      if (timerRef.current) {
        clearTimeout(timerRef.current);
        timerRef.current = null;
      }
      if (activeCleanupRef.current) {
        activeCleanupRef.current();
        activeCleanupRef.current = null;
      }

      // 리스너 해제 함수
      const cleanupListeners = () => {
        if (timerRef.current) {
          clearTimeout(timerRef.current);
          timerRef.current = null;
        }
        document.removeEventListener('visibilitychange', onVisibilityChange);
        window.removeEventListener('pagehide', onPageHide);
        activeCleanupRef.current = null;
      };

      // (라) 화면 숨김 감지 시 타이머 해제 및 잠금 해제 (성공이므로 onFail 미호출)
      const onVisibilityChange = () => {
        if (document.hidden || document.visibilityState === 'hidden') {
          cleanupListeners();
          if (isMountedRef.current) {
            setIsOpening(false);
          }
        }
      };

      const onPageHide = () => {
        cleanupListeners();
        if (isMountedRef.current) {
          setIsOpening(false);
        }
      };

      document.addEventListener('visibilitychange', onVisibilityChange);
      window.addEventListener('pagehide', onPageHide);
      activeCleanupRef.current = cleanupListeners;

      // (마) 1.5초 경과 시에도 화면이 살아있으면 리스너 해제 후 isOpening 해제 및 onFail() 호출
      timerRef.current = setTimeout(() => {
        cleanupListeners();
        if (isMountedRef.current) {
          setIsOpening(false);
        }
        onFail?.();
      }, INTENT_TIMEOUT_MS);

      window.location.href = intentUrl;
    } catch (err) {
      console.warn('[useOpenInExternalBrowser] Intent launch failed:', err);
      if (timerRef.current) {
        clearTimeout(timerRef.current);
        timerRef.current = null;
      }
      if (isMountedRef.current) {
        setIsOpening(false);
      }
      onFail?.();
    }
  }, [isOpening]);

  return { openInBrowser, isOpening };
}
